import { Link, createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  usePaymentResources,
  useSubmitPayment,
  useWithdrawalHistory,
  useWithdrawalProviders,
} from '@/api';
import { useTranslations } from '@/i18n/TranslationsProvider';
import { EmptyState, Icon, Spinner } from '@/ui/common';
import { WithdrawalFormPanel } from '@/ui/profile';

export const Route = createFileRoute('/profile/withdrawals/$providerId')({
  component: ProfileWithdrawalFormPage,
});

function ProfileWithdrawalFormPage() {
  const { t, i18n } = useTranslation();
  const { selectedLanguage } = useTranslations();
  const { providerId } = Route.useParams();
  const isTranslationReady =
    selectedLanguage === 'en' || i18n.hasResourceBundle(selectedLanguage, 'translation');
  const label = (key: string, fallback: string) =>
    isTranslationReady ? t(key, { defaultValue: fallback }) : fallback;

  const providers = useWithdrawalProviders(true);
  const provider = providers.findById(providerId);
  const resources = usePaymentResources('withdrawal', provider, Boolean(provider));
  const history = useWithdrawalHistory(Boolean(provider));
  const submitPayment = useSubmitPayment();

  return (
    <div className="flex flex-col gap-4 lg:gap-6">
      <Link
        to="/profile/withdrawals"
        className="inline-flex w-fit items-center gap-2 text-[13px] text-text-secondary hover:text-text-primary"
      >
        <Icon name="arrow-right" size={16} className="-scale-x-100" />
        {label('Withdraw', 'Withdraw')}
      </Link>

      <header className="flex flex-col gap-1 lg:gap-2">
        <h1 className="text-[18px] font-bold text-text-primary lg:text-[22px]">
          {label('Withdraw', 'Withdraw')}
        </h1>
        <p className="text-[13px] text-text-secondary">
          {label(
            'Enter payout details and confirm withdrawal',
            'Enter payout details and confirm withdrawal',
          )}
        </p>
      </header>

      {providers.isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner size="md" />
        </div>
      ) : !provider ? (
        <EmptyState
          icon="withdrawal"
          title={label('Provider not found', 'Provider not found')}
          description={label(
            'Choose another payment provider from the list.',
            'Choose another payment provider from the list.',
          )}
        />
      ) : (
        <WithdrawalFormPanel
          provider={provider}
          currency={providers.currency}
          resources={resources}
          history={history}
          submitPayment={submitPayment}
        />
      )}
    </div>
  );
}
